import React, { useState } from 'react';
import styled from 'styled-components';
import { useClerk, UserProfile } from '@clerk/clerk-react';
import { Modal } from '../ui/index.js';
import { Button } from '../ui/index.js';
import { theme } from '../../../config/theme';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/ClerkAuthContext';
import { classService, userService } from '../../api/migratedJuntoAPI';
import PaymentMethodsList from '../payment/PaymentMethodsList';
import { StaxProvider } from '../providers/StaxProvider';
import { staxConfig } from '../../../config/stax';

// Section components
const Section = styled.div`
  margin-bottom: ${theme.spacing.xl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  position: relative;
  justify-content: center;
  
  &::before {
    content: '';
    flex: 1;
    height: 1px;
    background: ${theme.colors.neutral.light};
    margin-right: ${theme.spacing.md};
  }
  
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${theme.colors.neutral.light};
    margin-left: ${theme.spacing.md};
  }
`;

const SectionTitle = styled.h3`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: 0.95rem;
  font-weight: 600;
  color: ${theme.colors.primary.text};
  margin: 0;
  white-space: nowrap;
`;

const SectionContent = styled.div`
  padding-left: 0;
`;

const InfoRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr auto;
  gap: ${theme.spacing.md};
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 100px 1fr auto;
    gap: ${theme.spacing.sm};
  }
`;

const InfoLabel = styled.span`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${theme.colors.primary.text};
  opacity: 0.7;
`;

const InfoValue = styled.span`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: 0.9rem;
  font-weight: 500;
  color: ${theme.colors.primary.text};
`;

const InfoGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SmallButton = styled(Button)`
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  font-size: 0.75rem;
  min-height: 32px;
  height: 32px;
`;

// Simple wrapper for styling
const UserProfileWrapper = styled.div`
  height: 100%;
  overflow: visible;
  
  .cl-userProfile-root {
    background: transparent;
    box-shadow: none;
    border: none;
    height: auto;
    overflow: visible;
  }

  .cl-button {
    font-family: ${theme.typography.button.fontFamily};
    border-radius: 50px;
    
    &:hover:not(:disabled) {
      background: ${theme.colors.primary.buttons} !important;
      color: ${theme.colors.primary.background} !important;
    }
  }
  
  /* Increase font size for all navigation buttons */
  .cl-navbarButton {
    font-size: 1rem !important;
    font-weight: 500 !important;
  }
`;

// LogoutSection removed - logout moved to tab

const ModalContentContainer = styled.div`
  margin: -32px;
  padding: 16px;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 12px;
  }
`;

// Helper components - CardLogo removed as PaymentMethodsList now handles card display

const CreditItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.sm} 0;
  border-bottom: 1px solid ${theme.colors.neutral.light};
  
  &:last-child {
    border-bottom: none;
  }
`;

// PaymentMethodItem removed - now using PaymentMethodCard component

// Status badge component
const StatusBadge = styled.span`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.status?.toLowerCase()) {
      case 'confirmed':
        return `
          background: ${theme.colors.accent.bright};
          color: ${theme.colors.accent.main};
        `;
      case 'waitlist':
        return `
          background: #fff3cd;
          color: #856404;
        `;
      case 'completed':
        return `
          background: ${theme.colors.neutral.light};
          color: ${theme.colors.primary.text};
          opacity: 0.8;
        `;
      case 'no-show':
        return `
          background: #f8d7da;
          color: #721c24;
        `;
      case 'cancelled':
        return `
          background: ${theme.colors.neutral.light};
          color: ${theme.colors.primary.text};
          opacity: 0.6;
        `;
      default:
        return `
          background: ${theme.colors.neutral.light};
          color: ${theme.colors.primary.text};
          opacity: 0.7;
        `;
    }
  }}
`;

// Enhanced InfoRow for classes with status
const ClassInfoRow = styled(InfoRow)`
  grid-template-columns: 140px 1fr auto auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 100px 1fr auto;
    gap: ${theme.spacing.sm};
    
    ${StatusBadge} {
      grid-column: 2;
      justify-self: start;
      margin-top: 4px;
    }
  }
`;

// Loading button state
const LoadingButton = styled(SmallButton)`
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Cancellation error popup
const ErrorPopup = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ErrorContent = styled.div`
  background: ${theme.colors.primary.background};
  padding: ${theme.spacing.xl};
  border-radius: 12px;
  max-width: 400px;
  margin: ${theme.spacing.md};
  text-align: center;
`;

const ErrorTitle = styled.h3`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: 1.2rem;
  font-weight: 600;
  color: ${theme.colors.primary.text};
  margin: 0 0 ${theme.spacing.md} 0;
`;

const ErrorMessage = styled.p`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${theme.colors.primary.text};
  opacity: 0.8;
  margin: 0 0 ${theme.spacing.lg} 0;
  line-height: 1.5;
`;

const ErrorButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

// Custom page components
const WalletPage = ({ onClose }) => {
  const navigate = useNavigate();
  const { accountData, refreshAccountData } = useAuth();
  // Payment processing state managed within payment components
  
  // Helper functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // formatDate and formatDateRange moved to MembershipPage component

  const formatExpirationDate = (dateString) => {
    if (!dateString) return 'No expiration';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Expired';
    if (diffDays <= 30) return `${diffDays} days left`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCreditDetails = () => {
    // Parse credit information with expiration dates
    return accountData?.wallet?.credit_details || [];
  };

  const getPaymentMethods = () => {
    return accountData?.wallet?.payment_methods || [];
  };

  // Payment method handlers for Stax integration
  const handleAddPaymentMethod = async (cardData) => {
    try {
      // Processing state handled by component
      
      // Send tokenized card data to backend
      const paymentMethodData = {
        token: cardData.token,
        card_details: cardData.cardDetails,
        billing_address: cardData.billingAddress,
        set_as_default: cardData.setAsDefault
      };
      
      await userService.addPaymentMethod(paymentMethodData);
      
      // Refresh account data to get updated payment methods
      if (refreshAccountData) {
        await refreshAccountData();
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error adding payment method:', error);
      throw error;
    } finally {
      // Processing state handled by component
    }
  };
  
  const handleDeletePaymentMethod = async (paymentMethodId) => {
    try {
      // Processing state handled by component
      
      await userService.removePaymentMethod(paymentMethodId);
      
      // Refresh account data
      if (refreshAccountData) {
        await refreshAccountData();
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting payment method:', error);
      throw error;
    } finally {
      // Processing state handled by component
    }
  };
  
  const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
    try {
      // Processing state handled by component
      
      // Call API to set default payment method
      // Note: This endpoint needs to be implemented in backend
      await userService.setDefaultPaymentMethod?.(paymentMethodId);
      
      // Refresh account data
      if (refreshAccountData) {
        await refreshAccountData();
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error setting default payment method:', error);
      // For now, just refresh data even if the API doesn't exist yet
      if (refreshAccountData) {
        await refreshAccountData();
      }
    } finally {
      // Processing state handled by component
    }
  };

  // const activeMemberships = getActiveMemberships(); // Unused in WalletPage
  // const recurringPurchases = getRecurringPurchases(); // Unused in WalletPage
  const creditDetails = getCreditDetails();
  const paymentMethods = getPaymentMethods();
  
  return (
    <div style={{ padding: '20px 0' }}>

      <Section>
        <SectionHeader>
          <SectionTitle>Wallet</SectionTitle>
        </SectionHeader>
        <SectionContent>
          <InfoRow>
            <InfoLabel>Balance</InfoLabel>
            <InfoValue style={{ fontSize: '1.2rem' }}>
              {formatCurrency(accountData?.wallet?.balance)}
            </InfoValue>
            <SmallButton variant="secondary">Add Funds</SmallButton>
          </InfoRow>
        </SectionContent>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>Credits</SectionTitle>
        </SectionHeader>
        <SectionContent>
          {creditDetails.length > 0 ? (
            creditDetails.map((credit, index) => (
              <CreditItem key={credit.id || index}>
                <InfoGroup>
                  <InfoValue>{credit.count || 1} credits</InfoValue>
                  <InfoLabel style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                    {credit.description || 'Class credits'}
                  </InfoLabel>
                </InfoGroup>
                <InfoLabel style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                  {formatExpirationDate(credit.expiry_date)}
                </InfoLabel>
              </CreditItem>
            ))
          ) : accountData?.wallet?.credits > 0 ? (
            <InfoRow>
              <InfoLabel>Available</InfoLabel>
              <InfoValue>
                {accountData.wallet.credits} credits
              </InfoValue>
              <SmallButton 
                variant="secondary"
                onClick={() => {
                  navigate('/purchase#credits');
                  onClose();
                }}
              >
                Buy Credits
              </SmallButton>
            </InfoRow>
          ) : (
            <InfoRow>
              <InfoLabel>Available</InfoLabel>
              <InfoValue>0 credits</InfoValue>
              <SmallButton 
                variant="secondary"
                onClick={() => {
                  navigate('/purchase#credits');
                  onClose();
                }}
              >
                Buy Credits
              </SmallButton>
            </InfoRow>
          )}
        </SectionContent>
      </Section>

      <Section>
        <StaxProvider publicKey={staxConfig.publicKey} environment={staxConfig.environment}>
          <PaymentMethodsList
            paymentMethods={paymentMethods}
            isLoading={false}
            error={null}
            onAddPaymentMethod={handleAddPaymentMethod}
            onDeletePaymentMethod={handleDeletePaymentMethod}
            onSetDefaultPaymentMethod={handleSetDefaultPaymentMethod}
            staxPublicKey={staxConfig.publicKey}
          />
        </StaxProvider>
      </Section>

    </div>
  );
};

const MembershipPage = ({ onClose }) => {
  const navigate = useNavigate();
  const { accountData } = useAuth();
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  };

  const getActiveMemberships = () => {
    if (!accountData?.memberships?.length) return [];
    return accountData.memberships.filter(m => 
      m.status === 'active' && new Date(m.end_date) > new Date()
    );
  };

  const getRecurringPurchases = () => {
    // This would come from API if available - currently empty as requested
    return accountData?.recurringPurchases || [];
  };

  const activeMemberships = getActiveMemberships();
  const recurringPurchases = getRecurringPurchases();

  return (
    <div style={{ padding: '20px 0' }}>
      <Section>
        <SectionHeader>
          <SectionTitle>Membership</SectionTitle>
        </SectionHeader>
        <SectionContent>
          {activeMemberships.length > 0 ? (
            activeMemberships.map((membership, index) => (
              <InfoRow key={membership.id || index}>
                <InfoLabel>
                  {membership.membership_type?.name || 'Membership'}
                </InfoLabel>
                <InfoGroup>
                  <InfoValue>Active</InfoValue>
                  <InfoLabel style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                    {formatDateRange(membership.start_date, membership.end_date)}
                  </InfoLabel>
                </InfoGroup>
                <SmallButton 
                  variant="secondary"
                  onClick={() => {
                    navigate('/purchase#memberships');
                    onClose();
                  }}
                >
                  Manage
                </SmallButton>
              </InfoRow>
            ))
          ) : (
            <InfoRow>
              <InfoLabel>Status</InfoLabel>
              <InfoValue>No active membership</InfoValue>
              <SmallButton 
                variant="secondary"
                onClick={() => {
                  navigate('/purchase#memberships');
                  onClose();
                }}
              >
                View Plans
              </SmallButton>
            </InfoRow>
          )}
        </SectionContent>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>Recurring Purchases</SectionTitle>
        </SectionHeader>
        <SectionContent>
          {recurringPurchases.length > 0 ? (
            recurringPurchases.map((purchase, index) => (
              <InfoRow key={purchase.id || index}>
                <InfoLabel>{purchase.name}</InfoLabel>
                <InfoGroup>
                  <InfoValue>{formatCurrency(purchase.amount)}</InfoValue>
                  <InfoLabel style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                    Next: {new Date(purchase.next_billing_date).toLocaleDateString()}
                  </InfoLabel>
                </InfoGroup>
                <SmallButton variant="secondary">Manage</SmallButton>
              </InfoRow>
            ))
          ) : (
            <InfoRow>
              <InfoLabel>Status</InfoLabel>
              <InfoValue>No recurring purchases</InfoValue>
              <div style={{ width: '80px' }}></div>
            </InfoRow>
          )}
        </SectionContent>
      </Section>
    </div>
  );
};

const PurchaseHistoryPage = () => {
  const { accountData } = useAuth();
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <div style={{ padding: '20px 0' }}>
      <Section>
        <SectionHeader>
          <SectionTitle>Purchase History</SectionTitle>
        </SectionHeader>
        <SectionContent>
          {accountData?.purchaseHistory?.length > 0 ? (
            accountData.purchaseHistory.map((purchase, index) => (
              <InfoRow key={purchase.id || index}>
                <InfoLabel>{formatDate(purchase.transaction_date)}</InfoLabel>
                <InfoGroup>
                  <InfoValue>{purchase.description || 'Purchase'}</InfoValue>
                  <InfoLabel style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                    {purchase.item_type || 'Item'}
                  </InfoLabel>
                </InfoGroup>
                <InfoGroup>
                  <InfoValue>{formatCurrency(purchase.amount)}</InfoValue>
                  <InfoLabel style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                    {purchase.status || 'Completed'}
                  </InfoLabel>
                </InfoGroup>
              </InfoRow>
            ))
          ) : (
            <InfoRow>
              <InfoLabel>History</InfoLabel>
              <InfoValue>No purchases yet</InfoValue>
              <div style={{ width: '80px' }}></div>
            </InfoRow>
          )}
        </SectionContent>
      </Section>
    </div>
  );
};

const ClassesPage = () => {
  const { accountData, refreshAccountData } = useAuth();
  const [cancellingBooking, setCancellingBooking] = useState(null);
  const [showCancelError, setShowCancelError] = useState(false);
  const [cancelErrorMessage, setCancelErrorMessage] = useState('');
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Sort upcoming classes by start time (earliest first)
  const getSortedUpcomingClasses = () => {
    if (!accountData?.upcomingClasses?.length) return [];
    return [...accountData.upcomingClasses].sort((a, b) => 
      new Date(a.class?.start_time) - new Date(b.class?.start_time)
    );
  };

  // Sort historical classes by start time (most recent first)
  const getSortedClassHistory = () => {
    if (!accountData?.classHistory?.length) return [];
    return [...accountData.classHistory].sort((a, b) => 
      new Date(b.class?.start_time) - new Date(a.class?.start_time)
    );
  };

  // Handle class cancellation
  const handleCancelClass = async (booking) => {
    if (!booking?.id) return;
    
    setCancellingBooking(booking.id);
    
    try {
      const result = await classService.cancelBooking(booking.id);
      
      if (result.success) {
        // Refresh account data to update the classes list
        await refreshAccountData();
      } else {
        throw new Error(result.message || 'Cancellation failed');
      }
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      
      // Show error popup for cancellation failures
      setCancelErrorMessage(
        error.message.includes('too close') || error.message.includes('time limit')
          ? `Cannot cancel this class due to timing restrictions. Please contact support for assistance.`
          : `Unable to cancel this class: ${error.message}. Please contact support if you need assistance.`
      );
      setShowCancelError(true);
    } finally {
      setCancellingBooking(null);
    }
  };

  // Get booking status with fallback
  const getBookingStatus = (booking) => {
    return booking?.status || 'confirmed';
  };

  // Get historical status with fallback
  const getHistoricalStatus = (booking) => {
    return booking?.status || 'completed';
  };

  const upcomingClasses = getSortedUpcomingClasses();
  const classHistory = getSortedClassHistory();

  return (
    <div style={{ padding: '20px 0' }}>
      {/* Cancellation Error Popup */}
      {showCancelError && (
        <ErrorPopup onClick={() => setShowCancelError(false)}>
          <ErrorContent onClick={(e) => e.stopPropagation()}>
            <ErrorTitle>Cannot Cancel Class</ErrorTitle>
            <ErrorMessage>{cancelErrorMessage}</ErrorMessage>
            <ErrorButtonContainer>
              <Button variant="primary" onClick={() => setShowCancelError(false)}>
                OK
              </Button>
            </ErrorButtonContainer>
          </ErrorContent>
        </ErrorPopup>
      )}

      <Section>
        <SectionHeader>
          <SectionTitle>Upcoming Classes</SectionTitle>
        </SectionHeader>
        <SectionContent>
          {upcomingClasses.length > 0 ? (
            upcomingClasses.map((booking, index) => (
              <ClassInfoRow key={booking.id} style={{ marginBottom: index < upcomingClasses.length - 1 ? theme.spacing.md : 0 }}>
                <InfoLabel>{formatDate(booking.class?.start_time)}</InfoLabel>
                <InfoGroup>
                  <InfoValue>{booking.class?.name || 'Class'}</InfoValue>
                  <InfoLabel style={{ fontSize: '0.8rem' }}>
                    {formatTime(booking.class?.start_time)} • {booking.class?.instructor?.name || 'Instructor'}
                  </InfoLabel>
                </InfoGroup>
                <StatusBadge status={getBookingStatus(booking)}>
                  {getBookingStatus(booking)}
                </StatusBadge>
                <LoadingButton 
                  variant="secondary"
                  disabled={cancellingBooking === booking.id}
                  onClick={() => handleCancelClass(booking)}
                >
                  {cancellingBooking === booking.id ? 'Cancelling...' : 'Cancel'}
                </LoadingButton>
              </ClassInfoRow>
            ))
          ) : (
            <InfoRow>
              <InfoLabel>Status</InfoLabel>
              <InfoValue>No upcoming classes</InfoValue>
              <div style={{ width: '80px' }}></div>
            </InfoRow>
          )}
        </SectionContent>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>Class History</SectionTitle>
        </SectionHeader>
        <SectionContent>
          {classHistory.length > 0 ? (
            classHistory.map((booking, index) => (
              <ClassInfoRow key={booking.id} style={{ marginBottom: index < classHistory.length - 1 ? theme.spacing.md : 0 }}>
                <InfoLabel>{formatDate(booking.class?.start_time)}</InfoLabel>
                <InfoGroup>
                  <InfoValue>{booking.class?.name || 'Class'}</InfoValue>
                  <InfoLabel style={{ fontSize: '0.8rem' }}>
                    {formatTime(booking.class?.start_time)} • {booking.class?.instructor?.name || 'Instructor'}
                  </InfoLabel>
                </InfoGroup>
                <StatusBadge status={getHistoricalStatus(booking)}>
                  {getHistoricalStatus(booking)}
                </StatusBadge>
                <div style={{ width: '80px' }}></div>
              </ClassInfoRow>
            ))
          ) : (
            <InfoRow>
              <InfoLabel>History</InfoLabel>
              <InfoValue>No classes taken</InfoValue>
              <div style={{ width: '80px' }}></div>
            </InfoRow>
          )}
        </SectionContent>
      </Section>
    </div>
  );
};

const CustomAccountPopup = ({ isOpen, onClose }) => {
  const { signOut } = useClerk();

  const handleLogout = () => {
    signOut();
    onClose();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      maxWidth="900px"
      fixedHeight={false}
      scrollable={true}
    >
      <ModalContentContainer>
        <div style={{ flex: 1, minHeight: 0 }}>
          <UserProfileWrapper>
            <UserProfile>
              <UserProfile.Page 
                label="Membership" 
                labelIcon="🎫"
                url="membership"
              >
                <MembershipPage onClose={onClose} />
              </UserProfile.Page>
              
              <UserProfile.Page 
                label="Wallet" 
                labelIcon="💳"
                url="wallet"
              >
                <WalletPage onClose={onClose} />
              </UserProfile.Page>
              
              <UserProfile.Page 
                label="Classes" 
                labelIcon="📅"
                url="classes"
              >
                <ClassesPage />
              </UserProfile.Page>
              
              <UserProfile.Page 
                label="Purchase History" 
                labelIcon="📄"
                url="purchase-history"
              >
                <PurchaseHistoryPage />
              </UserProfile.Page>
              
              <UserProfile.Page 
                label="Logout" 
                labelIcon="🚪"
                url="logout"
              >
                <div style={{ padding: '40px 0', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                  <Button
                    onClick={handleLogout}
                    variant="primary"
                    size="large"
                    style={{ 
                      padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                      fontSize: '1rem',
                      minHeight: '48px',
                      minWidth: '120px'
                    }}
                  >
                    Logout
                  </Button>
                </div>
              </UserProfile.Page>
            </UserProfile>
          </UserProfileWrapper>
        </div>
      </ModalContentContainer>
    </Modal>
  );
};

export default CustomAccountPopup;